import React from 'react';
import makeStyles from '@mui/styles/makeStyles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MUIButton from '@mui/material/Button';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import AppShell from 'components/AppShell';
import Hero from './Home/Hero';
import CTABanner from './Home/CTABanner';
import Session from 'utils/Session';

// ─── Feature cards shown as bottomContent in Hero ─────────────────────────────

const HERO_FEATURES = [
    {
        icon: DescriptionOutlinedIcon,
        title: 'Comprehensive Guides',
        desc: 'In-depth guides covering strategies for every question type',
    },
    {
        icon: FileDownloadOutlinedIcon,
        title: 'Downloadable PDFs',
        desc: 'Print-friendly worksheets and practice materials',
    },
];

const useFeatureCardStyles = makeStyles(theme => ({
    featureCard: {
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        backgroundColor: 'rgba(255,255,255,0.06)',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: '12px',
        padding: '20px 24px',
        flex: 1,
        minWidth: '260px',
        maxWidth: '400px',
    },
    featureIconBox: {
        width: '40px',
        height: '40px',
        borderRadius: '10px',
        backgroundColor: 'rgba(255,255,255,0.1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
}));

function HeroFeatureCards() {
    const classes = useFeatureCardStyles();

    return (
        <Box
            sx={{
                display: 'flex',
                gap: '20px',
                flexWrap: 'wrap',
                justifyContent: 'center',
                width: '100%',
                maxWidth: '840px',
                mt: '16px',
            }}
        >
            {HERO_FEATURES.map(({ icon: Icon, title, desc }) => (
                <Box key={title} className={classes.featureCard}>
                    <Box className={classes.featureIconBox}>
                        <Icon sx={{ color: '#0284C5', fontSize: '1.2rem' }} />
                    </Box>
                    <Box>
                        <Typography
                            component="div"
                            variant="body02-bold"
                            sx={{ color: '#FFFFFF', mb: '4px' }}
                        >
                            {title}
                        </Typography>
                        <Typography
                            component="div"
                            variant="body02-regular"
                            sx={{ color: 'rgba(255,255,255,0.7)', lineHeight: 1.5 }}
                        >
                            {desc}
                        </Typography>
                    </Box>
                </Box>
            ))}
        </Box>
    );
}

// ─── IELTS Free Resources Section ────────────────────────────────────────────

const S3_BASE = 'https://languify-assets-v02.s3.ap-south-1.amazonaws.com/IELTS_resources';

const PDF_FILES = [
    { name: 'Vocabulary_Book_Feb_2023.pdf',       label: 'Vocabulary_Book_Feb_2023.pdf',       type: 'PDF Document', size: '3.2 MB' },
    { name: 'self-assessment_tool.pdf',            label: 'self-assessment_tool.pdf',            type: 'PDF Document', size: '1.4 MB' },
    { name: 'Self_Study_eBook.pdf',                label: 'Self_Study_eBook.pdf',                type: 'PDF Document', size: '4.8 MB' },
    { name: 'master_the_basic.pdf',                label: 'master_the_basic.pdf',                type: 'PDF Document', size: '2.1 MB' },
    { name: 'information-for-candidates.pdf',      label: 'information-for-candidates.pdf',      type: 'PDF Document', size: '0.9 MB' },
    { name: 'IELTS_Academic_Reading_Test.pdf',     label: 'IELTS_Academic_Reading_Test.pdf',     type: 'PDF Document', size: '2.4 MB' },
    { name: 'Grammar_Workbook_Feb_2023.pdf',       label: 'Grammar_Workbook_Feb_2023.pdf',       type: 'PDF Document', size: '3.6 MB' },
    { name: 'A_Starter_Study_Plan_for_IELTS_Success.pdf', label: 'A_Starter_Study_Plan_for_IELTS_Success.pdf', type: 'PDF Document', size: '1.1 MB' },
];

const downloadFile = (fileName) => {
    const a = document.createElement('a');
    a.href = `${S3_BASE}/${fileName}`;
    a.download = fileName;
    a.target = '_blank';
    a.rel = 'noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
};

const RESOURCE_ITEMS = [
    'Task 1 structure templates',
    'Task 2 essay frameworks',
    'High-score vocabulary lists',
    'Grammar checklist for writers',
];

const useResourcesStyles = makeStyles(theme => ({
    root: {
        width: '100%',
        backgroundColor: theme.palette.shades?.['clr-offWhite'] || '#FCFAFA',
        padding: '80px 32px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        [theme.breakpoints.down('md')]: {
            padding: '48px 16px',
        },
    },
    card: {
        width: '100%',
        maxWidth: '640px',
        backgroundColor: theme.palette.shades?.['clr-white-900'] || '#FFFFFF',
        border: `1px solid ${theme.palette.neutral?.['clr-200'] || '#D3D8DE'}`,
        borderRadius: '16px',
        overflow: 'hidden',
    },
    cardHeader: {
        padding: '28px 28px 24px',
    },
    iconBox: {
        width: '44px',
        height: '44px',
        borderRadius: '10px',
        backgroundColor: '#E0F2FE',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '16px',
    },
    fileRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        padding: '14px 28px',
        borderTop: `1px solid ${theme.palette.neutral?.['clr-200'] || '#D3D8DE'}`,
    },
    fileIconBox: {
        width: '38px',
        height: '38px',
        borderRadius: '8px',
        backgroundColor: '#E0F2FE',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    fileDownloadBtn: {
        minWidth: 'unset',
        width: '36px',
        height: '36px',
        borderRadius: '8px',
        backgroundColor: '#0284C5',
        color: '#FFFFFF',
        padding: 0,
        flexShrink: 0,
        boxShadow: 'none',
        '&:hover': {
            backgroundColor: '#026BA1',
            boxShadow: 'none',
        },
    },
}));

function IELTSFreeResources() {
    const classes = useResourcesStyles();

    return (
        <Box className={classes.root}>
            {/* Section heading */}
            <Typography
                component="h2"
                variant="h3-bold"
                color="neutral.clr-900"
                sx={{ textAlign: 'center', mb: '12px' }}
            >
                IELTS Free Resources
            </Typography>
            <Typography
                component="p"
                variant="body01-regular"
                color="neutral.clr-500"
                sx={{ textAlign: 'center', maxWidth: '520px', mb: '40px', lineHeight: 1.6 }}
            >
                Targeted materials for each IELTS component to help you prepare effectively.
            </Typography>

            {/* Resource card */}
            <Box className={classes.card}>

                {/* ── Card header ── */}
                <Box className={classes.cardHeader}>
                    <Box className={classes.iconBox}>
                        <DescriptionOutlinedIcon sx={{ color: '#0284C5', fontSize: '1.3rem' }} />
                    </Box>

                    <Typography component="h3" variant="h6-bold" color="neutral.clr-900" sx={{ mb: '6px' }}>
                        IELTS Resources
                    </Typography>

                    <Typography component="p" variant="body02-regular" color="neutral.clr-500" sx={{ mb: '20px' }}>
                        Essay templates, vocabulary lists, and Task 1/Task 2 guides
                    </Typography>

                    {/* Checklist */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {RESOURCE_ITEMS.map(item => (
                            <Box key={item} sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <CheckCircleOutlineIcon sx={{ color: '#0284C5', fontSize: '1rem', flexShrink: 0 }} />
                                <Typography component="span" variant="body02-regular" color="neutral.clr-700">
                                    {item}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* ── Individual file rows ── */}
                {PDF_FILES.map((file) => (
                    <Box key={file.name} className={classes.fileRow}>
                        {/* PDF icon */}
                        <Box className={classes.fileIconBox}>
                            <DescriptionOutlinedIcon sx={{ color: '#0284C5', fontSize: '1.1rem' }} />
                        </Box>

                        {/* Name + meta */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                                component="div"
                                variant="body02-semiBold"
                                color="neutral.clr-900"
                                sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                            >
                                {file.label}
                            </Typography>
                            <Typography component="div" variant="body02-regular" color="neutral.clr-400">
                                {file.type} • {file.size}
                            </Typography>
                        </Box>

                        {/* Per-file download button */}
                        <MUIButton
                            variant="contained"
                            disableElevation
                            onClick={() => downloadFile(file.name)}
                            className={classes.fileDownloadBtn}
                        >
                            <FileDownloadOutlinedIcon sx={{ fontSize: '1.1rem' }} />
                        </MUIButton>
                    </Box>
                ))}

            </Box>
        </Box>
    );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

function FreeStudyMaterial() {
    return (
        <AppShell hideAnnouncement placeAtTop backgroundColor="#12111A">
            <Hero
                badgeText="Free with All Plans"
                heading1="Free IELTS Study Material"
                heading2={null}
                subtitle="Access comprehensive study resources, strategies, and practice materials for all four IELTS sections - included free with every BandBoost plan."
                noteText="Available in Basic, Advanced, and Premium plans"
                hideCta
                hideSocialProof
                features={[]}
                bottomContent={<HeroFeatureCards />}
            />
            <IELTSFreeResources />
            <CTABanner
                heading="Get Started with Free Study Materials"
                subtitle="Sign up for free and access all study resources instantly."
                ctaText="Sign Up Now →"
                ctaHref="/register"
                hidePrimaryCta={Session.isLoggedIn()}
                secondaryCtaText="Try a Free Mock Test"
                secondaryCtaHref="/practice#practice-by-tests"
            />
        </AppShell>
    );
}

export default FreeStudyMaterial;
